import angular from "angular";

import { Arrays, CostAccountingDimensionDefinition, CostAccountingDimension, CostAccountingDimensionScope } from "@salaxy/core";

import { SessionService} from "../../services";

/**
 * Handles the user interaction for entering cost accounting data.
 */
export class CostAccountingController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService"];

  /** Scope for the cost accounting: calculation or row. */
  public scope: CostAccountingDimensionScope;

  /** Dimension values to edit.*/
  public current: CostAccountingDimension[];

  /** Possible upper level (e.g. calculation) dimensions that are inherited */
  private _inherited: CostAccountingDimension[];

  /** Bindable read-only */
  public readOnly?: boolean;

  /** CostAccountingDimensionDefinition including label, type and options */
  public dimensions: CostAccountingDimensionDefinition[];

  /**
   * Style of inputs.
   * If null or empty, displays normal input.
   * - "openeClose" shows readOnly-like input with toggle button to open the field for editing.
   */
  public inputMode: "" | "openClose";

  private _definitions:  CostAccountingDimensionDefinition[] = [];
  private _options: { [key: string] : Array<{
    /** Option value. */
    value: any,
    /** Option text.  */
    text: string,
    /** Option description. */
    title?: string,
  }> } = {};

  private _values:  { [key: string] : Array<CostAccountingDimension> } = {};

  private _inheritedValues:  { [key: string] : Array<CostAccountingDimension> } = {};
  private _obsoleteValues: Array<CostAccountingDimension> = [];
  private _newDimensions: CostAccountingDimension[] = [];

  /**
   * Creates a new CalcCostAccountingController
   * @ignore
   */
  constructor(
    protected  sessionService: SessionService
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
   // empty
  }

  /** Copy of inherited dimensions */
  public get inherited(): CostAccountingDimension[] {
    return this._inherited;
  }

  /** Copy of inherited dimensions */
  public set inherited(value: CostAccountingDimension[])  {
    this._inherited = angular.copy(value);
  }

  /** Returns applicable dimensions*/
  public get definitions(): CostAccountingDimensionDefinition[] {
    const d = (this.dimensions ?? this.sessionService.session?.settings?.costAccounting?.dimensions ?? [])
    .filter( (x) =>
      // no hidden scope
      x.scope !== CostAccountingDimensionScope.Hidden &&
      (
        // show if scope definition for dimension not given
        ( !x.scope || x.scope === CostAccountingDimensionScope.None) ||
        // or if scope definition for use given
        (!this.scope || this.scope === CostAccountingDimensionScope.None) ||
        // or this scope
        (this.scope === x.scope)
      )
    );
    this.modifyOptions(d, this._definitions);
    return this._definitions;
  }

  /**
   * Returns true, if the dimension exists.
   * @param id Id of the dimension.
   * @returns True, if the dimension with given id exists.
   */
  public getDefinition(id: string) {
    return this.definitions.find( (x) => x.id == id);
  }

   /**
    * Returns total percentage for the dimension
    * @param id Name of the dimension.
    */
  public getSum(id: string ): number {
      return Arrays.sum((this.current ?? []).filter((x) => x.id == id), (x: any) => x.percent ?? 0);
  }

   /**
    * Returns available options.
    * @param id Id of the dimension.
    */
  public getOptions(id: string) {
    if (!this._options[id]) {
      this._options[id] = [];
    }

    let newOptions = [];
    const dimensionForOptions = this.definitions.find((x) => x.id === id);
    if (dimensionForOptions) {
      newOptions = dimensionForOptions.options.filter((x) =>
        x.scope !== CostAccountingDimensionScope.Hidden &&
        (
          // show if scope definition for dimension not given
          ( !x.scope || x.scope === CostAccountingDimensionScope.None) ||
          // or if scope definition for use given
          (!this.scope || this.scope === CostAccountingDimensionScope.None) ||
          // or this scope
          (this.scope === x.scope)
        )
      )
      .map( (x) => ({ value: x.value, text: x.text }));
    }

    this.modifyOptions(newOptions, this._options[id]);
    return this._options[id];
  }

  /**
   * Returns true if options exists for the dimension.
   * @param id Id of the dimension.
   * @param value Value of the dimension.
   * @returns True, if an option exists for the value.
   */
  public hasOptions(id: string, value: string): boolean {
    const opts = this.getOptions(id);
    if (!value) {
      return opts.filter(x => x.value).length > 0;
    }
    return !!opts.find((x) => x.value == value);
  }

   /**
    * Returns single value.
    * @param id Id of the dimension.
    */
   public getSingleValue(id: string) {
    let singleValue = this.current?.find((x) => x.id === id) ?? this._newDimensions.find((x) => x.id === id);
    if (!singleValue && !this.readOnly) {
      singleValue = { id };
      this._newDimensions.push(singleValue);
    }
    return singleValue;
  }

  /**
   * Sets single value.
   * @param id Id of the dimension.
   * @param value Value of the dimension.
   */
    public setSingleValue(id: string, value: string) {
      const singleValue = this.current?.find((x) => x.id === id);
      if (singleValue) {
        singleValue.value = value;
      }
      else {
        this.current.push({ id, value });
      }
    }

  /**
   * Returns current values.
   * @param id Id of the dimension.
   */
  public getValues(id: string) {
    if (!this._values[id]) {
      this._values[id] = [];
    }

    const values = (this.current ?? []).filter((x) => x.id === id);
    this.modifyOptions(values, this._values[id]);

    return this._values[id];
  }

  /**
   * Sets values.
   * @param values Values of the dimension.
   */
  public setValues(values: CostAccountingDimension[]) {
    this.current.push( ... angular.copy(values));
  }

  /**
   * Deletes value from the dimensions
   * @param value Value to delete
   */
  public deleteValue(value: CostAccountingDimension) {
    const index = this.current.indexOf(value);
    if (index >= 0) {
      this.current.splice(index,1);
    }
  }

  /**
   * Returns true if the dimension values are inherited.
   * @param id Id of the dimension.
   */
  public isInherited(id: string) {
    if ( !this.current?.some((x) => x.id === id && !!x.value)) {
      return !!this.cleanDimensions(this.inherited)?.some((x) => x.id === id);
    }
    return false;
  }

  /**
   * Returns inherited single value.
   * @param id Id of the dimension.
   */
  public getInheritedSingleValue(id: string) {
    const singleValue = (this.cleanDimensions(this.inherited) ?? []).find((x) => x.id === id);
    return singleValue;
  }

  /**
   * Returns inherited values.
   * @param id Id of the dimension.
   */
   public getInheritedValues(id: string) {

    if (!this._inheritedValues[id]) {
      this._inheritedValues[id] = [];
    }

    const values = (this.cleanDimensions(this.inherited) ?? []).filter((x) => x.id === id);
    this.modifyOptions(values, this._inheritedValues[id]);

    return this._inheritedValues[id];
  }

  /**
   * Returns total percentage for the dimension for inherited values
   * @param id Name of the dimension.
   */
   public getInheritedSum(id: string ): number {
      return Arrays.sum((this.cleanDimensions(this.inherited)  ?? []).filter((x) => x.id == id), (x: any) => x.percent ?? 0);
  }

  /**
   * Returns current obsolete values.
   */
    public get obsoleteValues() {
      if (this.current){
        const obsolete = this.current.filter((x) => !this._definitions.find((d) => d.id === x.id)).sort((a,b) => (a.id ?? "").localeCompare(b.id ?? "") );
        this.modifyOptions(obsolete, this._obsoleteValues);
      }
      return this._obsoleteValues;
    }

  /**
   * Returns true if the values contains obsolete dimensions.
   * @returns True if the values contains obsolete dimensions.
   */
  public get hasObsoleteValues(): boolean {
    return this.obsoleteValues.length > 0;
  }

  private modifyOptions(
    source: any[],
    target: any[]) {
    if (angular.equals(source, target)) {
      return;
    }
    target.splice(0, target.length);
    target.push(...source);
  }

  private cleanDimensions(dimensions: CostAccountingDimension[]) {
    if (!dimensions) {
      return dimensions;
    }
    const cleaned = dimensions.filter(x => x.value && x.id);
    return cleaned;
  }
}
